import {
  Box,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { IconTriangleExclamation } from 'central-icons/IconTriangleExclamation';
import type { MultiValue } from 'chakra-react-select';
import {
  type Control,
  Controller,
  type ControllerRenderProps,
  type FormState,
  type UseFormRegister,
  type UseFormWatch,
} from 'react-hook-form';

import { useGetLoggedInUser, useIsRevelioAdmin } from '@revelio/auth';
import {
  type AdditionalPermissionsInput,
  type ApiKeyEndpoint,
  type Client,
  type CustomTaxonomyEnum,
  MetadataKey,
  type PostingSource,
  Role,
  type Tab,
} from '@revelio/data-access';
import { Button, FormField, Input, Select, Switch, Text } from '@revelio/ui';

import { FieldGroup } from '../../shared/ui/admin/groups/field/field-group';
import {
  countConfiguredEndpoints,
  parseRequestsPerSecond,
  RequestsPerSecondModal,
} from './requests-per-second-modal';
import {
  defaultPipelineOptions,
  defaultTabOptions,
  getMetadataValue,
  type OptionValues,
  type PipelineSelectOptions,
  pipelineOptions,
  type ReportSelectOptions,
  tabOptions,
} from './utils/helpers';

export type TabSelectOption = { label: string; value: Tab; default?: boolean };
export type PipelineSelectOption = {
  label: string;
  value: string;
  default?: boolean;
  preventAdditions?: boolean;
};
export type ReportSelectOption = {
  label: string;
  value: string;
  default?: boolean;
  preventAdditions?: boolean;
  isFixed?: boolean;
};
export type PostingSourceSelectOption = {
  label: string;
  value: PostingSource;
  default?: boolean;
};
export type ClientFormValues = {
  active: boolean;
  tabs: TabSelectOption[];
  live: boolean;
  linkup_postings: boolean;
  num_seats: string;
  requests_per_second: Partial<Record<ApiKeyEndpoint, string>>;
  requests_per_month: string;
  client_name: string;
  report_types: ReportSelectOptions;
  pipeline_types: PipelineSelectOptions;
  posting_sources: PostingSourceSelectOption[];
  s3_location: string;
  snowflake_database: string;
  snowflake_schema: string;
  custom_taxonomy: { label: string; value: CustomTaxonomyEnum };
  additional_permissions?: AdditionalPermissionsInput;
};

type AdditionalPermissionKey = keyof AdditionalPermissionsInput;

const additionalPermissionFields: Array<{
  key: AdditionalPermissionKey;
  label: string;
  inputType: 'boolean';
}> = [
  {
    key: 'allow_cusip',
    label: 'Allow CUSIP',
    inputType: 'boolean',
  },
  {
    key: 'contact_info',
    label: 'Contact Info',
    inputType: 'boolean',
  },
];

type Props = {
  clientToEdit?: Client | null;
  reportOptions?: ReportSelectOption[];
  title?: string;
  description?: string;
  register: UseFormRegister<ClientFormValues> | (() => object);
  control?: Control<ClientFormValues, object>;
  watch: UseFormWatch<ClientFormValues>;
  errors?: FormState<ClientFormValues>['errors'];
  actions?: JSX.Element;
  readonly?: boolean;
};

const ClientForm = ({
  clientToEdit,
  reportOptions,
  title,
  description,
  register,
  control,
  watch,
  errors,
  actions,
  readonly,
}: Props) => {
  const { loggedInUser } = useGetLoggedInUser();
  const { isRevelioAdmin } = useIsRevelioAdmin();
  const isClientAdmin = loggedInUser.role === Role.ClientAdmin;
  const shouldShowApiAccess = isRevelioAdmin || isClientAdmin;
  const isApiAccessReadOnly = !isRevelioAdmin || readonly;
  const {
    isOpen: isAdditionalOptionsOpen,
    onOpen: onAdditionalOptionsOpen,
    onClose: onAdditionalOptionsClose,
  } = useDisclosure();
  const {
    isOpen: isRequestsPerSecondOpen,
    onOpen: onRequestsPerSecondOpen,
    onClose: onRequestsPerSecondClose,
  } = useDisclosure();

  const isAddingNewClient = clientToEdit === null;

  const hasDeliverablesTab = watch('tabs', [])?.some(
    (tab) => tab?.value === 'deliverables'
  );

  const hasReportsTab = watch('tabs', [])?.some(
    (tab) => tab?.value === 'reports'
  );

  const isTrialClient = !watch('live', clientToEdit?.live ?? false);
  const watchedAdditionalPermissions = watch(
    'additional_permissions',
    clientToEdit?.data_builder_configuration?.additional_permissions ?? {}
  );
  const hasAdditionalOptionsConfigured = additionalPermissionFields.some(
    ({ key }) => watchedAdditionalPermissions?.[key] === true
  );

  const toast = useToast();

  const onChangeTabs =
    (field: ControllerRenderProps<ClientFormValues, 'tabs'>) =>
    (selectedOptions: MultiValue<OptionValues>) => {
      const selectedLabels = selectedOptions.map((option) => option.label);
      const hasSentiment = selectedLabels.includes('Sentiment');
      const hasSentimentV1 = selectedLabels.includes('Sentiment_V1');

      if (hasSentiment && hasSentimentV1) {
        toast({
          title: 'Tab conflict',
          description:
            'You cannot add Sentiment and Sentiment_V1 at the same time.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
        return;
      }

      field.onChange(selectedOptions);
    };

  return (
    <Stack
      className="client-form"
      justifyContent="space-between"
      spacing="0"
      height="100%"
      key={clientToEdit?.client_name}
    >
      <Box flex="1" minHeight="0" overflowY="auto" px="2" py="2">
        <FieldGroup title={title} description={description}>
          <VStack spacing="2" align="stretch">
            {isRevelioAdmin && !isAddingNewClient && (
              <FormField label="Active">
                <Switch
                  id="active"
                  size="sm"
                  inactiveLabel="Off"
                  label="On"
                  disabled={!isRevelioAdmin || readonly}
                  defaultChecked={clientToEdit?.active}
                  {...register('active')}
                />
              </FormField>
            )}

            {(isRevelioAdmin || clientToEdit?.num_seats !== '0') && (
              <Box mt="2">
                <FormField label="Number of seats">
                  <Input
                    type="number"
                    wrapperStyle={{ maxWidth: 80 }}
                    disabled={!isRevelioAdmin || readonly}
                    defaultValue={clientToEdit?.num_seats || '0'}
                    {...register('num_seats')}
                  />
                </FormField>
              </Box>
            )}

            <Box mt="2">
              <FormField label="Tabs">
                <Controller
                  name="tabs"
                  control={control}
                  defaultValue={
                    !isAddingNewClient && clientToEdit?.tabs?.length
                      ? (clientToEdit.tabs.map((tab) =>
                          tabOptions.find((o) => o.value === tab)
                        ) as TabSelectOption[])
                      : defaultTabOptions
                  }
                  render={({ field }) => (
                    <Select<OptionValues, true>
                      {...field}
                      isDisabled={!isRevelioAdmin || readonly}
                      isClearable={isRevelioAdmin && !readonly}
                      isMulti
                      name="tabs"
                      options={tabOptions}
                      placeholder="Select tabs..."
                      closeMenuOnSelect={false}
                      noOptionsMessage={() => 'No Tabs'}
                      defaultValue={defaultTabOptions}
                      onChange={onChangeTabs(field)}
                    />
                  )}
                />
              </FormField>
            </Box>

            {hasReportsTab && (
              <Stack
                display={readonly ? 'none' : 'inherit'}
                width="100%"
                borderWidth="1px"
                borderRadius="md"
                p="2"
                mt="2"
                boxShadow="sm"
                color="gray.800"
              >
                <Heading as="h5" size="sm">
                  Report Builder Configuration
                </Heading>
                <Box mt="2">
                  <Controller
                    name="report_types"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (isTrialClient) {
                          return true;
                        }

                        if ((value?.length ?? 0) === 0) {
                          return 'At least one report type is required.';
                        }

                        return true;
                      },
                    }}
                    defaultValue={reportOptions?.filter(
                      (option) => option.default
                    )}
                    render={({ field, fieldState }) => {
                      const addableOptions = reportOptions?.filter(
                        (option) =>
                          !option.preventAdditions ||
                          field.value?.some((o) => o.label === option.label)
                      );

                      return (
                        <FormField
                          label="Report Types"
                          errors={
                            fieldState.error?.message
                              ? [fieldState.error.message]
                              : undefined
                          }
                        >
                          <Select<OptionValues, true>
                            {...field}
                            isDisabled={!isRevelioAdmin || readonly}
                            isClearable={
                              isRevelioAdmin &&
                              !readonly &&
                              !addableOptions?.some((o) => o.isFixed)
                            }
                            isMulti
                            isInvalid={!!fieldState.error}
                            name="report_types"
                            options={addableOptions}
                            placeholder="Select report types..."
                            closeMenuOnSelect={false}
                            noOptionsMessage={() => 'No report types'}
                          />
                        </FormField>
                      );
                    }}
                  />
                </Box>
              </Stack>
            )}

            {hasDeliverablesTab && (
              <Stack
                display={readonly ? 'none' : 'inherit'}
                width="100%"
                borderWidth="1px"
                borderRadius="md"
                p="2"
                mt="2"
                boxShadow="sm"
                color="gray.800"
              >
                <Heading as="h5" size="sm" color="text.primary">
                  Data Builder Configuration
                </Heading>

                <Box mt="2">
                  <Controller
                    name="pipeline_types"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (isTrialClient) {
                          return true;
                        }

                        if ((value?.length ?? 0) === 0) {
                          return 'At least one dataset is required.';
                        }

                        return true;
                      },
                    }}
                    defaultValue={defaultPipelineOptions}
                    render={({ field, fieldState }) => {
                      const addableOptions = pipelineOptions.filter(
                        (option) =>
                          !option.preventAdditions ||
                          field.value?.some((o) => o.label === option.label)
                      );
                      return (
                        <FormField
                          label="Subscribed Datasets"
                          errors={
                            fieldState.error?.message
                              ? [fieldState.error.message]
                              : undefined
                          }
                        >
                          <Select<OptionValues, true>
                            {...field}
                            isDisabled={!isRevelioAdmin || readonly}
                            isClearable={isRevelioAdmin && !readonly}
                            isMulti
                            isInvalid={!!fieldState.error}
                            name="pipeline_types"
                            options={addableOptions}
                            placeholder="Select datasets..."
                            closeMenuOnSelect={false}
                            noOptionsMessage={() => 'No Datasets'}
                          />
                        </FormField>
                      );
                    }}
                  />
                </Box>

                <Box mt="2">
                  <FormField label="S3 Bucket Location">
                    <Input
                      type="text"
                      disabled={!isRevelioAdmin || readonly}
                      defaultValue={
                        clientToEdit?.data_builder_configuration?.s3_location
                          ?.s3_bucket || ''
                      }
                      {...register('s3_location')}
                    />
                  </FormField>
                </Box>

                <Box mt="2">
                  <FormField
                    label="Snowflake Database"
                    errors={
                      errors?.snowflake_database?.message
                        ? [errors.snowflake_database.message]
                        : undefined
                    }
                  >
                    <Input
                      type="text"
                      isInvalid={!!errors?.snowflake_database}
                      disabled={!isRevelioAdmin || readonly}
                      defaultValue={
                        clientToEdit?.data_builder_configuration
                          ?.snowflake_location?.snowflake_db || ''
                      }
                      {...register('snowflake_database', {
                        pattern: {
                          value: /^(?!user_)/,
                          message:
                            'Delivery to USER databases is not supported\u2019',
                        },
                      })}
                    />
                  </FormField>
                </Box>

                <Box mt="2">
                  <FormField label="Snowflake Schema">
                    <Input
                      type="text"
                      disabled={!isRevelioAdmin || readonly}
                      defaultValue={
                        clientToEdit?.data_builder_configuration
                          ?.snowflake_location?.snowflake_schema || ''
                      }
                      {...register('snowflake_schema')}
                    />
                  </FormField>
                </Box>

                <FormField label="Additional Permissions">
                  <Flex alignItems="center" gap="2" flexWrap="wrap">
                    {hasAdditionalOptionsConfigured ? (
                      <Text
                        fontSize="sm"
                        weight="semibold"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                      >
                        <IconTriangleExclamation />
                        Client has additional options turned on
                      </Text>
                    ) : (
                      <Text fontSize="sm">
                        No additional options are configured
                      </Text>
                    )}

                    <Button
                      variant="ghost"
                      color="primary"
                      size="sm"
                      type="button"
                      onClick={onAdditionalOptionsOpen}
                      disabled={!isRevelioAdmin || readonly}
                    >
                      Edit
                    </Button>
                  </Flex>
                </FormField>
              </Stack>
            )}

            <FormField
              label="Data Access"
              supportingText="Bypasses trial end dates and enables full data access for ONE_YEAR_LAG trial clients."
            >
              <Switch
                id="live"
                size="sm"
                inactiveLabel="Trial"
                label="Live"
                disabled={!isRevelioAdmin || readonly}
                defaultChecked={clientToEdit?.live}
                {...register('live')}
              />
            </FormField>

            <FormField label="Website Postings">
              <Switch
                id="linkup_postings"
                size="sm"
                inactiveLabel="Off"
                label="On"
                disabled={true}
                defaultChecked={
                  isAddingNewClient ? false : clientToEdit?.linkup_postings
                }
                {...register('linkup_postings')}
              />
            </FormField>
          </VStack>
        </FieldGroup>

        {shouldShowApiAccess && (
          <Box mt="2">
            <Controller
              name="requests_per_second"
              control={control}
              defaultValue={parseRequestsPerSecond(
                getMetadataValue(
                  clientToEdit?.metadata,
                  MetadataKey.RequestsPerSecond
                )
              )}
              render={({ field: rpsField }) => (
                <Controller
                  name="requests_per_month"
                  control={control}
                  defaultValue={
                    getMetadataValue(
                      clientToEdit?.metadata,
                      MetadataKey.RequestsPerMonth
                    ) ?? ''
                  }
                  render={({ field: rpmField }) => {
                    const configuredCount = countConfiguredEndpoints(
                      rpsField.value ?? {}
                    );
                    const monthlyValue = rpmField.value ?? '';
                    const hasMonthly = monthlyValue.trim() !== '';
                    const isCustomized = configuredCount > 0 || hasMonthly;
                    const summaryText = (() => {
                      const parts: string[] = [];
                      if (configuredCount === 1) {
                        parts.push('1 endpoint customized');
                      } else if (configuredCount > 1) {
                        parts.push(
                          `${configuredCount} endpoints with specific limits`
                        );
                      }
                      if (hasMonthly) {
                        parts.push(`Monthly cap: ${monthlyValue}`);
                      }
                      if (parts.length === 0) {
                        return 'Default rate limits (10/sec, no monthly cap)';
                      }
                      return parts.join(' \u00b7 ');
                    })();

                    return (
                      <FormField label="API Access Limits">
                        <Flex alignItems="center" gap="2" flexWrap="wrap">
                          <Text
                            fontSize="sm"
                            weight={isCustomized ? 'semibold' : 'regular'}
                          >
                            {summaryText}
                          </Text>
                          <Button
                            variant="ghost"
                            color="primary"
                            size="sm"
                            type="button"
                            onClick={onRequestsPerSecondOpen}
                            disabled={isApiAccessReadOnly}
                            style={{
                              height: 'auto',
                              minHeight: 0,
                              padding: '2px 6px',
                            }}
                          >
                            Edit
                          </Button>
                          <RequestsPerSecondModal
                            isOpen={isRequestsPerSecondOpen}
                            onClose={onRequestsPerSecondClose}
                            value={rpsField.value ?? {}}
                            onChange={rpsField.onChange}
                            monthlyValue={monthlyValue}
                            onMonthlyChange={rpmField.onChange}
                            isReadOnly={isApiAccessReadOnly}
                          />
                        </Flex>
                      </FormField>
                    );
                  }}
                />
              )}
            />
          </Box>
        )}
      </Box>

      {actions && (
        <Box
          flexShrink={0}
          borderTopWidth="1px"
          borderColor="gray.200"
          px="2"
          py="2"
        >
          {actions}
        </Box>
      )}

      <Modal
        isOpen={isAdditionalOptionsOpen}
        onClose={onAdditionalOptionsClose}
        size="md"
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Text fontSize="bg" weight="semibold" includePadding>
              Additional Data Builder Permissions
            </Text>
            <Text fontSize="sm">
              These permissions should be off for most clients. Only enable them
              for a good reason.
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing="4">
              {additionalPermissionFields.map((field) => {
                if (field.inputType !== 'boolean') {
                  return null;
                }

                return (
                  <Box key={field.key}>
                    <Controller
                      name={`additional_permissions.${field.key}` as const}
                      control={control}
                      defaultValue={
                        clientToEdit?.data_builder_configuration
                          ?.additional_permissions?.[field.key] === true
                      }
                      render={({ field: switchField }) => (
                        <Switch
                          label={field.label}
                          size="md"
                          name={switchField.name}
                          checked={switchField.value === true}
                          onChange={(e) =>
                            switchField.onChange(e.target.checked)
                          }
                          onBlur={switchField.onBlur}
                          disabled={!isRevelioAdmin || readonly}
                        />
                      )}
                    />
                  </Box>
                );
              })}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={onAdditionalOptionsClose}>
              Done
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
};

export default ClientForm;
